import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import LabelInputField from "../../components/core-components/label-input-field/LabelInputField";
import CoreButtons from "../../components/core-components/button/CoreButtons";
import ForgotPasswordModal from "../../components/reusable-components/modals/forgot-password-modal/ForgotPasswordModal";
import { useAppDispatch } from "../../hooks/storeHooks";
import { ROUTE_PATHS } from "../../routes";
import { forgotPasswordThunk } from "../../store/user/userThunk";
import { BUTTON_COLOR, BUTTON_VARIANT } from "../../types/coreButtonTypes";
import typography from "../../theme/typography";
import palette from "../../theme/palette";
import IsMobile from "../../hooks/isMobile";
import { REDUX_REQUEST_STATUS } from "../../types/commonTypes";
import { useToast } from "../../context/ToasterContext";
import { FORGOT_EMAIL_SCHEMA } from "../../utils/yup-schemas/yupSchemas";

export default function ForgotPasswordPage() {
	const { showToast } = useToast();
	const isMobile = IsMobile();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [email, setEmail] = useState("");
	const [emailError, setEmailError] = useState("");
	const [modalOpen, setModalOpen] = useState(false);
	const [loading, setLoading] = useState(false);

	const isValidEmail = useMemo(() => {
		const trimmed = email.trim();
		if (!trimmed) return false;
		try {
			FORGOT_EMAIL_SCHEMA.validateSync(trimmed);
			return true;
		} catch {
			return false;
		}
	}, [email]);

	const handleContinue = async () => {
		const trimmed = email.trim();
		try {
			FORGOT_EMAIL_SCHEMA.validateSync(trimmed);
		} catch (err) {
			if (yup.ValidationError.isError(err)) {
				setEmailError(err.message);
			}
			return;
		}
		setEmailError("");
		setModalOpen(true);
		setLoading(true);
		const resp = await dispatch(forgotPasswordThunk({ email: trimmed }));
		setLoading(false);

		if (resp.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
			showToast("Email sent successfully.");
		}
	};

	const handleOkay = () => {
		setModalOpen(false);
		navigate(ROUTE_PATHS.LOGIN);
	};

	return (
		<>
			<Card elevation={0} sx={{ width: "100%", boxShadow: "none", border: "none", background: "none" }}>
				<CardContent sx={{ p: isMobile ? 1.5 : 3, pt: 0, width: "100%" }}>
					<Stack spacing={2} sx={{ alignItems: "center", mb: 4 }}>
						<Typography sx={{ textAlign: "center", fontFamily: typography.fontSecondary,
								fontSize: isMobile ? 32 : 38,
								fontWeight: 600,
								color: palette.secondary.main, }}>
							Forgot password
						</Typography>
						<Typography
							variant="body1" sx={{ color: palette.grey[950], textAlign: "center", fontFamily: typography.fontPrimaryGeneralSans,
								fontWeight: typography.fontWeightRegular, }}>
							Oops. It happens to the best of us. Input your email address to fix the issue.
						</Typography>
					</Stack>

					<Box sx={{ maxWidth: 500, width: "100%", mx: "auto", pb: isMobile ? "88px" : 0 }}>
						<Stack component="form" noValidate spacing={2}>
							<LabelInputField
								id="email"
								label="Email"
								type="email"
								placeholder="Enter your email"
								value={email}
								onChange={(e) => {
									setEmail(e.target.value);
									if (emailError) setEmailError("");
								}}
								onBlur={() => {
									const trimmed = email.trim();
									if (!trimmed) return;
									try {
										FORGOT_EMAIL_SCHEMA.validateSync(trimmed);
										setEmailError("");
									} catch (err) {
										if (yup.ValidationError.isError(err)) setEmailError(err.message);
									}
								}}
								error={!!emailError}
								errorTextOnly
								helperText={emailError}
								className="form-group"
							/>
							<Box
								sx={
									isMobile
										? {
												position: "fixed",
												bottom: 24,
												left: 0,
												right: 0,
												px: 2,
												zIndex: 1,
											}
										: undefined
								}
							>
								<CoreButtons
									buttonText="Continue"
									fullWidth
									coreBtnPill
									variant={BUTTON_VARIANT.CONTAINED}
									color={BUTTON_COLOR.PRIMARY}
									onclick={handleContinue}
									disabled={!isValidEmail || loading}
									loading={loading}
									sx={{
										fontFamily: typography.fontPrimaryGeneralSans,
										fontWeight: typography.fontWeightMedium,
									}}
								/>
							</Box>
						</Stack>
					</Box>
				</CardContent>
			</Card>
			<ForgotPasswordModal open={modalOpen} onClose={() => setModalOpen(false)} onOkay={handleOkay} />
		</>
	);
}
